import React, { useState, useRef, useEffect } from "react";
import { Upload, Check, ArrowRight, ArrowLeft, Image as ImageIcon, BookOpen, Download, Pencil, Lock } from "lucide-react";
import { StepDots } from "./GoogleBits";
import A4Sheet from "./A4Sheet";
import A4Document from "./A4Document";
import { todayBR } from "../lib/format";
import { getDominantColor } from "../lib/color";
import { usePlan } from "../context/PlanContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { toast } from "sonner";

const FONTS = [
  { key: "sans", label: "Sans Moderna", cls: "tpl-sans", sample: "Plus Jakarta" },
  { key: "serif", label: "Serif Editorial", cls: "tpl-serif-head", sample: "Instrument" },
  { key: "mono", label: "Técnica Mono", cls: "tpl-mono", sample: "JetBrains" },
  { key: "inter", label: "Inter Neutra", cls: "font-inter", sample: "Inter" },
  { key: "dm", label: "DM Suave", cls: "font-dm", sample: "DM Sans" },
  { key: "syne", label: "Syne Display", cls: "font-syne", sample: "Syne" },
];

const COLORS = [
  { name: "Grafite", value: "#0f172a" },
  { name: "Azul Basiq", value: "#4A3BFA" },
  { name: "Esmeralda", value: "#059669" },
  { name: "Índigo", value: "#4f46e5" },
  { name: "Rubi", value: "#e11d48" },
  { name: "Âmbar", value: "#d97706" },
];

const LAYOUTS = [
  { id: "minimal_classic", name: "Clássico Minimalista", tag: "Header limpo, tipografia tradicional" },
  { id: "modern_editorial", name: "Editorial Moderno", tag: "Acento colorido e cabeçalho suave" },
  { id: "corporate_bold", name: "Corporativo Sofisticado", tag: "Hierarquia marcante e assinatura" },
  { id: "compact_tech", name: "Técnico Essencial", tag: "Grade densa para muitos itens" },
  { id: "creative_studio", name: "Estúdio Criativo", tag: "Títulos serifados, muito respiro" },
  { id: "glass_aurora", name: "Aurora Glass", tag: "Cartão translúcido, brilho multicolor" },
  { id: "gradient_wave", name: "Gradiente", tag: "Faixa em gradiente vibrante" },
  { id: "soft_pastel", name: "Pastel", tag: "Blocos suaves e arredondados" },
  { id: "mono_grid", name: "Grade", tag: "Estrutura em grade técnica" },
  { id: "prestige_dark", name: "Prestígio", tag: "Barra lateral escura premium" },
];

const sampleData = {
  client_name: "Maria Santos",
  client_details: "Ateliê Santos · (11) 90000-0000",
  title: "Proposta de Serviços",
  intro: "Segue proposta comercial conforme alinhado em nossa conversa.",
  items: [
    { description: "Desenvolvimento do layout", quantity: 1, unit_price: 3500 },
    { description: "Configuração e integração", quantity: 1, unit_price: 1200 },
    { description: "Suporte mensal", quantity: 1, unit_price: 800 },
  ],
  discount: 0,
  tax: 0,
  payment_terms: "50% na aprovação e 50% na entrega.",
  notes: "Proposta válida conforme prazo indicado.",
  issue_date: todayBR(),
  valid_until: "—",
};

const PreviewA4 = ({ layout, company, theme, active, onClick, name, tag, locked }) => {
  const W = 250;
  const scale = W / 794;
  return (
    <button
      onClick={onClick}
      data-testid={`layout-option-${layout}`}
      className={`group relative text-left rounded-2xl p-2.5 transition-all duration-300 ${
        active ? "ring-2 ring-offset-2 ring-slate-900 bg-white" : "ring-1 ring-slate-200 bg-white hover:ring-slate-300 hover:-translate-y-1"
      }`}
    >
      <div className="relative overflow-hidden rounded-lg border border-slate-100" style={{ width: W, height: 353, background: "#fff" }}>
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: 794 }}>
          <A4Sheet layout={layout} company={company} theme={theme} data={sampleData} editable={false} />
        </div>
        {locked && (
          <div className="absolute inset-0 bg-white/55 backdrop-blur-[1px] grid place-items-center" data-testid={`layout-lock-${layout}`}>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 bg-white ring-1 ring-slate-200 rounded-full px-2.5 py-1 shadow-sm">
              <Lock size={12} /> Premium
            </span>
          </div>
        )}
        {active && !locked && (
          <div className="absolute top-2 right-2 grid place-items-center w-6 h-6 rounded-full google-gradient text-white shadow">
            <Check size={14} strokeWidth={3} />
          </div>
        )}
      </div>
      <div className="px-1.5 pt-3 pb-1">
        <div className="text-[13px] font-semibold text-slate-900">{name}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">{tag}</div>
      </div>
    </button>
  );
};

export default function Onboarding({ initial, onDone, mode, allowCancel, onCancel }) {
  const isEdit = mode === "edit";
  const { canPersonalize, isAllowedModel, openUpgrade } = usePlan();
  const [step, setStep] = useState(0);
  const [logo, setLogo] = useState(initial?.logo || null);
  const [avatarBg, setAvatarBg] = useState(initial?.theme?.avatarBg || null);
  const [font, setFont] = useState(initial?.font || "sans");
  const [primary, setPrimary] = useState(initial?.colors?.primary || "#0f172a");
  const [company, setCompany] = useState(
    initial?.company || { name: "", doc: "", phone: "", email: "", address: "", web: "" }
  );
  const [layout, setLayout] = useState(initial?.layout || "minimal_classic");
  const [cover, setCover] = useState(initial?.doc?.cover ?? false);
  const [coverTitle, setCoverTitle] = useState(initial?.doc?.coverTitle || "Proposta Comercial");
  const [headerLabel, setHeaderLabel] = useState(initial?.doc?.headerLabel || "Orçamento");
  const [logoRight, setLogoRight] = useState(initial?.doc?.logoRight ?? false);
  const [logoSize, setLogoSize] = useState(initial?.doc?.logoSize || "md");
  const fileRef = useRef(null);
  const [previewW, setPreviewW] = useState(() => Math.min(460, (typeof window !== "undefined" ? window.innerWidth : 460) - 40));
  useEffect(() => {
    const onR = () => setPreviewW(Math.min(460, window.innerWidth - 40));
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  const previewScale = previewW / 794;

  const theme = { primary, font, headerLabel, logoRight, logoSize };
  const docTheme = { primary, font, headerLabel, logoRight, logoSize, cover, coverTitle };
  const companyWithLogo = { ...company, logo };

  const handleLogo = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Selecione um arquivo de imagem");
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogo(e.target.result);
      getDominantColor(e.target.result).then((c) => c && setAvatarBg(c));
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (logo && !avatarBg) getDominantColor(logo).then((c) => c && setAvatarBg(c));
  }, [logo, avatarBg]);

  const finish = () => {
    let fLayout = layout, fFont = font, fCover = cover;
    if (!isAllowedModel(fLayout)) fLayout = "minimal_classic";
    if (!canPersonalize) { fFont = "sans"; fCover = false; }
    onDone({ logo, font: fFont, colors: { primary }, company, layout: fLayout, avatarBg, doc: { cover: fCover, coverTitle, headerLabel, logoRight, logoSize } });
  };

  const selectFont = (key) => {
    if (!canPersonalize && key !== "sans") return openUpgrade("personalization");
    setFont(key);
  };
  const selectLayout = (id) => {
    if (!isAllowedModel(id)) return openUpgrade("model");
    setLayout(id);
  };
  const toggleCover = (v) => {
    if (!canPersonalize) return openUpgrade("personalization");
    setCover(v);
  };

  const next = () => setStep((s) => Math.min(s + 1, 6));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const cu = (k, v) => setCompany((c) => ({ ...c, [k]: v }));

  return (
    <div className="min-h-screen pt-14 flex flex-col items-center px-5">
      {step > 0 && step < 5 && (
        <div className="sticky top-14 z-30 self-stretch -mx-5 px-4 sm:px-5 py-3 bg-[#fafafa]/85 backdrop-blur-md border-b border-slate-200/60" data-testid="onboarding-step-header">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
            <Button variant="ghost" onClick={back} className="rounded-full text-slate-500 shrink-0">
              <ArrowLeft size={16} className="mr-1" /> Voltar
            </Button>
            <StepDots total={4} current={step - 1} />
            <div className="flex items-center gap-1.5 shrink-0">
              {step === 1 && (
                <Button variant="ghost" onClick={next} className="rounded-full text-slate-400 text-[13px] hidden sm:inline-flex">Pular por agora</Button>
              )}
              <Button onClick={next} disabled={step === 4 && !company.name} data-testid="onboarding-next-btn" className="rounded-full h-10 px-5 sm:px-6 bg-slate-900 hover:bg-slate-800 disabled:opacity-40">
                {step === 4 ? "Ver os modelos" : "Continuar"} <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="w-full max-w-3xl pt-8" />

      {/* Step 0: welcome */}
      {step === 0 && (
        <div className="flex flex-col items-center text-center float-up">
          <div className="relative mb-10" data-testid="onboarding-hero-preview" style={{ perspective: 1200 }}>
            {/* floating accent chips */}
            <div className="absolute -left-6 sm:-left-16 top-16 hidden sm:flex items-center gap-2 bg-white/90 backdrop-blur ring-1 ring-slate-200 rounded-full px-3 py-1.5 shadow-lg z-20" style={{ animation: "floatUp 0.6s ease both, bob 4s ease-in-out infinite 0.6s" }}>
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full google-gradient opacity-60" /><span className="relative inline-flex rounded-full h-2 w-2 google-gradient" /></span>
              <span className="text-[11px] font-medium text-slate-600">Gerando com IA</span>
            </div>
            <div className="absolute -right-4 sm:-right-14 bottom-24 hidden sm:flex items-center gap-1.5 bg-white/90 backdrop-blur ring-1 ring-slate-200 rounded-full px-3 py-1.5 shadow-lg z-20" style={{ animation: "floatUp 0.6s ease both 0.2s, bob 4.6s ease-in-out infinite 0.9s" }}>
              <span className="grid place-items-center w-4 h-4 rounded-full bg-emerald-500 text-white"><Check size={11} strokeWidth={3} /></span>
              <span className="text-[11px] font-medium text-slate-600">Pronto para enviar</span>
            </div>

            {/* the premium A4 quote mock */}
            <div className="a4-sheet relative overflow-hidden w-[80vw] max-w-[360px]" style={{ padding: 26, minHeight: 0, transform: "rotateX(6deg) rotateZ(-1.2deg)", transformStyle: "preserve-3d" }}>
              {/* top accent + generating bar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl google-gradient shadow-sm" />
                  <div className="space-y-1.5">
                    <div className="h-2.5 w-24 rounded-full bg-slate-200" />
                    <div className="h-2 w-16 rounded-full bg-slate-100" />
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] font-semibold tracking-[0.2em] text-slate-400">PROPOSTA</div>
                  <div className="h-2 w-14 rounded-full bg-slate-100 mt-1.5 ml-auto" />
                </div>
              </div>

              <div className="mt-4 h-1 w-full rounded-full overflow-hidden bg-slate-100">
                <div className="h-full w-1/2 google-progress" />
              </div>

              {/* client + title (blurred) */}
              <div className="mt-6 space-y-2">
                <div className="h-2 w-20 rounded-full bg-slate-100" />
                <div className="h-4 w-3/4 rounded-md bg-slate-200" style={{ filter: "blur(0.6px)" }} />
                <div className="h-2.5 w-1/2 rounded-full bg-slate-100" style={{ filter: "blur(0.6px)" }} />
              </div>

              {/* items table (blurred rows) */}
              <div className="mt-6 rounded-xl ring-1 ring-slate-100 overflow-hidden">
                <div className="flex items-center justify-between px-3.5 py-2" style={{ background: "rgba(74,59,250,0.06)" }}>
                  <div className="h-2 w-16 rounded-full bg-slate-200/70" />
                  <div className="h-2 w-10 rounded-full bg-slate-200/70" />
                </div>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center justify-between px-3.5 py-3 border-t border-slate-50" style={{ animation: `pulseSoft 2.4s ease-in-out infinite ${i * 0.25}s` }}>
                    <div className="space-y-1.5" style={{ width: "58%" }}>
                      <div className="h-2.5 rounded-full bg-slate-200" style={{ width: `${80 - i * 12}%`, filter: "blur(0.5px)" }} />
                      <div className="h-2 rounded-full bg-slate-100" style={{ width: `${55 - i * 8}%` }} />
                    </div>
                    <div className="h-3 w-16 rounded-md bg-slate-200" style={{ filter: "blur(0.5px)" }} />
                  </div>
                ))}
              </div>

              {/* totals */}
              <div className="mt-5 flex justify-end">
                <div className="w-40 rounded-xl px-4 py-3" style={{ background: "rgba(15,23,42,0.03)" }}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total</span>
                    <div className="h-3.5 w-16 rounded-md google-gradient opacity-80" />
                  </div>
                </div>
              </div>

              {/* inner action buttons (interactive look) */}
              <div className="mt-6 flex items-center gap-2">
                <div className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-slate-900 text-white text-[11px] font-medium shadow-sm">
                  <Download size={12} /> Baixar PDF
                </div>
                <div className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white ring-1 ring-slate-200 text-slate-600 text-[11px] font-medium">
                  <Pencil size={12} /> Editar
                </div>
                <div className="ml-auto grid place-items-center w-8 h-8 rounded-full ring-1 ring-slate-200 bg-white text-slate-500">
                  <BookOpen size={13} />
                </div>
              </div>

              {/* soft sheen */}
              <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(255,255,255,0) 40%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 60%)", mixBlendMode: "screen" }} />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 max-w-lg leading-tight">
            {isEdit ? "Ajuste o modelo desta empresa" : "Vamos criar o modelo da sua empresa"}
          </h1>
          <p className="text-slate-500 mt-4 max-w-md text-[15px] leading-relaxed">
            Em poucos passos aplicamos sua marca e geramos 10 layouts profissionais. Você escolhe o seu favorito.
          </p>
          <Button onClick={next} data-testid="start-onboarding-btn" className="mt-9 rounded-full h-11 px-7 text-[14px] bg-slate-900 hover:bg-slate-800">
            Começar <ArrowRight size={16} className="ml-1" />
          </Button>
          {allowCancel && (
            <button onClick={onCancel} data-testid="onboarding-cancel" className="mt-4 text-[13px] text-slate-400 hover:text-slate-700 transition-colors">
              Cancelar
            </button>
          )}
        </div>
      )}

      {/* Step 1: logo */}
      {step === 1 && (
        <div className="w-full max-w-md float-up">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 text-center">Sua logomarca</h2>
          <p className="text-slate-500 text-center mt-2 text-[14px]">Envie a logo que aparecerá nos orçamentos.</p>
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleLogo(e.dataTransfer.files?.[0]);
            }}
            data-testid="onboarding-upload-logo"
            className="mt-8 cursor-pointer rounded-2xl border-2 border-dashed border-slate-200 hover:border-slate-300 hover:bg-white transition-all p-10 grid place-items-center text-center"
          >
            {logo ? (
              <img src={logo} alt="logo" className="max-h-24 object-contain" />
            ) : (
              <div className="text-slate-400">
                <div className="mx-auto w-12 h-12 rounded-xl bg-slate-100 grid place-items-center mb-3">
                  <Upload size={20} />
                </div>
                <div className="text-[13px] font-medium text-slate-600">Arraste ou clique para enviar</div>
                <div className="text-[11px] mt-1">PNG, JPG ou SVG</div>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => handleLogo(e.target.files?.[0])} />
          {logo && (
            <button onClick={() => { setLogo(null); setAvatarBg(null); }} className="mt-3 text-[12px] text-slate-400 hover:text-rose-500 flex items-center gap-1 mx-auto">
              <ImageIcon size={13} /> Remover logo
            </button>
          )}
        </div>
      )}

      {/* Step 2: font */}
      {step === 2 && (
        <div className="w-full max-w-lg float-up">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 text-center">Escolha a tipografia</h2>
          <p className="text-slate-500 text-center mt-2 text-[14px]">Define o caráter do documento.</p>
          <div className="mt-8 grid gap-3">
            {FONTS.map((f) => {
              const locked = !canPersonalize && f.key !== "sans";
              return (
                <button
                  key={f.key}
                  onClick={() => selectFont(f.key)}
                  data-testid={`font-option-${f.key}`}
                  className={`flex items-center justify-between rounded-xl px-5 py-4 transition-all ${
                    font === f.key ? "ring-2 ring-slate-900 bg-white" : "ring-1 ring-slate-200 bg-white hover:ring-slate-300"
                  }`}
                >
                  <span className="flex items-center gap-2 text-[13px] font-medium text-slate-500">
                    {locked && <Lock size={13} className="text-slate-400" data-testid={`font-lock-${f.key}`} />}
                    {f.label}
                  </span>
                  <span className={`${f.cls} text-2xl ${locked ? "text-slate-300" : "text-slate-900"}`}>{f.sample}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 3: colors */}
      {step === 3 && (
        <div className="w-full max-w-lg float-up">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 text-center">Cor da sua marca</h2>
          <p className="text-slate-500 text-center mt-2 text-[14px]">Usada em detalhes e destaques do orçamento.</p>
          <div className="mt-8 grid grid-cols-3 gap-3">
            {COLORS.map((c) => (
              <button
                key={c.value}
                onClick={() => setPrimary(c.value)}
                data-testid={`color-option-${c.name}`}
                className={`flex items-center gap-3 rounded-xl px-4 py-3.5 transition-all ${
                  primary === c.value ? "ring-2 ring-slate-900 bg-white" : "ring-1 ring-slate-200 bg-white hover:ring-slate-300"
                }`}
              >
                <span className="w-6 h-6 rounded-full ring-1 ring-black/5" style={{ background: c.value }} />
                <span className="text-[13px] font-medium text-slate-700">{c.name}</span>
              </button>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3 justify-center">
            <span className="text-[12px] text-slate-400">Personalizada</span>
            <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} data-testid="custom-color" className="w-9 h-9 rounded-lg cursor-pointer bg-transparent border border-slate-200" />
          </div>
        </div>
      )}

      {/* Step 4: company */}
      {step === 4 && (
        <div className="w-full max-w-lg float-up">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 text-center">Dados da empresa</h2>
          <p className="text-slate-500 text-center mt-2 text-[14px]">Aparecem no cabeçalho de todos os orçamentos.</p>
          <div className="mt-8 grid gap-3">
            <Field label="Nome / Razão Social" value={company.name} onChange={(v) => cu("name", v)} testid="company-name" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="CNPJ / CPF" value={company.doc} onChange={(v) => cu("doc", v)} testid="company-doc" />
              <Field label="Telefone" value={company.phone} onChange={(v) => cu("phone", v)} testid="company-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="E-mail" value={company.email} onChange={(v) => cu("email", v)} testid="company-email" />
              <Field label="Site / Instagram" value={company.web} onChange={(v) => cu("web", v)} testid="company-web" />
            </div>
            <Field label="Endereço" value={company.address} onChange={(v) => cu("address", v)} testid="company-address" />
          </div>
        </div>
      )}

      {/* Step 5: layout selection */}
      {step === 5 && (
        <div className="w-full max-w-5xl float-up pb-16">
          <div className="sticky top-14 z-30 -mx-5 px-5 py-3 bg-[#fafafa]/85 backdrop-blur-md border-b border-slate-200/60" data-testid="layout-step-header">
            <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
              <Button variant="ghost" onClick={back} className="rounded-full text-slate-500 shrink-0">
                <ArrowLeft size={16} className="mr-1" /> Voltar
              </Button>
              <div className="text-center min-w-0 hidden sm:block">
                <h2 className="text-base sm:text-2xl font-semibold tracking-tight text-slate-900 whitespace-nowrap truncate">Escolha seu modelo</h2>
                <p className="text-slate-500 mt-0.5 text-[13px] hidden sm:block">Layouts com sua marca já aplicada. Selecione o padrão.</p>
              </div>
              <Button onClick={() => setStep(6)} data-testid="preview-onboarding-btn" className="rounded-full h-10 px-5 sm:px-7 bg-slate-900 hover:bg-slate-800 shrink-0">
                Pré-visualizar <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-5">
            {LAYOUTS.map((l) => (
              <PreviewA4
                key={l.id}
                layout={l.id}
                name={l.name}
                tag={l.tag}
                company={companyWithLogo}
                theme={theme}
                active={layout === l.id}
                locked={!isAllowedModel(l.id)}
                onClick={() => selectLayout(l.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Step 6: preview & approve */}
      {step === 6 && (
        <div className="w-full max-w-5xl float-up pb-20">
          <div className="sticky top-14 z-30 -mx-5 px-5 py-3 bg-[#fafafa]/85 backdrop-blur-md border-b border-slate-200/60" data-testid="preview-step-header">
            <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
              <Button variant="ghost" onClick={() => setStep(5)} className="rounded-full text-slate-500 shrink-0">
                <ArrowLeft size={16} className="mr-1" /> Voltar
              </Button>
              <div className="text-center min-w-0 hidden sm:block">
                <h2 className="text-base sm:text-2xl font-semibold tracking-tight text-slate-900 whitespace-nowrap truncate">É assim que vai ficar?</h2>
                <p className="text-slate-500 mt-0.5 text-[13px] hidden sm:block">Ajuste os detalhes. Se estiver bom, aprove e vamos para o chat.</p>
              </div>
              <Button onClick={finish} data-testid="finish-onboarding-btn" className="rounded-full h-10 px-5 sm:px-7 bg-slate-900 hover:bg-slate-800 shrink-0">
                {isEdit ? "Salvar alterações" : "Está ok, aprovar"} <Check size={16} className="ml-1.5" />
              </Button>
            </div>
          </div>

          <div className="mt-8 flex flex-col lg:flex-row gap-8 items-start justify-center">
            {/* controls */}
            <div className="w-full lg:w-72 shrink-0 bg-white ring-1 ring-slate-200 rounded-2xl p-5 space-y-5 order-2 lg:order-1">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="grid place-items-center w-9 h-9 rounded-lg ring-1 ring-slate-200 bg-slate-50 overflow-hidden shrink-0">
                    {logo ? <img src={logo} alt="" className="max-w-[30px] max-h-[30px] object-contain" /> : <ImageIcon size={15} className="text-slate-400" />}
                  </span>
                  <span className="text-[13px] font-medium text-slate-800 truncate">Logo</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => fileRef.current?.click()} data-testid="preview-change-logo" className="text-[11.5px] font-medium text-slate-500 hover:text-slate-900 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors">
                    {logo ? "Trocar" : "Adicionar"}
                  </button>
                  {logo && (
                    <button type="button" onClick={() => { setLogo(null); setAvatarBg(null); }} data-testid="preview-remove-logo" className="text-[11.5px] text-slate-400 hover:text-rose-600 px-2 py-1 rounded-md hover:bg-rose-50 transition-colors">
                      Remover
                    </button>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="preview-logo-input" onChange={(e) => { handleLogo(e.target.files?.[0]); e.target.value = ""; }} />
                </div>
              </div>
              <div>
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">Fonte</span>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="preview-font-picker">
                  {FONTS.map((f) => {
                    const locked = !canPersonalize && f.key !== "sans";
                    return (
                      <button key={f.key} type="button" onClick={() => selectFont(f.key)} data-testid={`preview-font-${f.key}`} className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full transition-all ${f.cls} ${font === f.key ? "bg-slate-900 text-white" : locked ? "bg-slate-100 text-slate-400" : "bg-slate-100 text-slate-500 hover:text-slate-800"}`}>
                        {locked && <Lock size={10} />}{f.sample}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="h-px bg-slate-100" />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookOpen size={16} className="text-slate-500" />
                  <span className="text-[13px] font-medium text-slate-800">Capa e contracapa</span>
                </div>
                {canPersonalize ? (
                  <Switch checked={cover} onCheckedChange={toggleCover} data-testid="cover-switch" />
                ) : (
                  <button onClick={() => openUpgrade("personalization")} data-testid="cover-switch-locked" className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1 hover:bg-slate-200 transition-colors">
                    <Lock size={12} /> Premium
                  </button>
                )}
              </div>
              {cover && (
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">Título da capa</span>
                  <Input value={coverTitle} onChange={(e) => setCoverTitle(e.target.value)} data-testid="cover-title-input" className="mt-1.5 h-10 rounded-xl border-slate-200" />
                </label>
              )}
              <label className="block">
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">Rótulo do cabeçalho</span>
                <Input value={headerLabel} onChange={(e) => setHeaderLabel(e.target.value)} data-testid="header-label-input" className="mt-1.5 h-10 rounded-xl border-slate-200" />
              </label>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-medium text-slate-800">Logo à direita</span>
                <Switch checked={logoRight} onCheckedChange={setLogoRight} data-testid="logo-side-switch" />
              </div>
              {logo && (
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-medium text-slate-800">Tamanho da logo</span>
                  <div className="flex gap-1" data-testid="onb-logo-size-group">
                    {[["sm", "P"], ["md", "M"], ["lg", "G"]].map(([k, l]) => (
                      <button key={k} onClick={() => setLogoSize(k)} data-testid={`onb-logo-size-${k}`} className={`w-8 h-7 rounded-lg text-[11px] font-semibold transition-colors ${logoSize === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:text-slate-900"}`}>{l}</button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">Modelo</span>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {LAYOUTS.map((l) => {
                    const locked = !isAllowedModel(l.id);
                    return (
                      <button key={l.id} onClick={() => selectLayout(l.id)} className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full transition-all ${layout === l.id ? "bg-slate-900 text-white" : locked ? "bg-slate-100 text-slate-400" : "bg-slate-100 text-slate-500 hover:text-slate-800"}`}>
                        {locked && <Lock size={10} />}{l.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* live preview */}
            <div className="order-1 lg:order-2" style={{ width: previewW }}>
              <div style={{ width: previewW, height: `${((cover ? 3 : 1) * 1123 + (cover ? 2 * 24 : 0)) * previewScale}px`, overflow: "hidden" }}>
                <div style={{ transform: `scale(${previewScale})`, transformOrigin: "top left", width: 794 }}>
                  <A4Document layout={layout} company={companyWithLogo} theme={docTheme} data={sampleData} editable={false} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wide">{label}</span>
    <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white focus-visible:ring-slate-900/10" />
  </label>
);
